import { Helmet } from "react-helmet-async";

/**
 * SEO Meta Component
 * 
 * Handles OpenGraph and Twitter Card meta tags for social media sharing.
 * Pulls user-uploaded branding/portfolio images when available.
 */
const SEOMeta = ({ 
  title = "MyHairChair - Book Your Style",
  description = "The booking platform built BY Barbers and Stylists, FOR Barbers and Stylists. Skip the fees, pay direct.",
  image = "https://static.prod-images.emergentagent.com/jobs/2f9006eb-b332-428d-86e9-eb851b9d0232/images/63d58951e5ab7e31ae4c087a16e2dd8d29cd67a8c02b18e2b7a2b5eca234ad44.png",
  url = typeof window !== 'undefined' ? window.location.href : "https://myhairchair.com",
  type = "website",
  twitterCard = "summary_large_image",
  stylistName = null,
  shopName = null,
  profileImage = null
}) => {
  // Use profile image if provided, otherwise use default
  const ogImage = profileImage || image;
  
  // Build dynamic title and description for stylist profiles
  const dynamicTitle = stylistName 
    ? `Book with ${stylistName} on MyHairChair`
    : title;
  
  const dynamicDescription = stylistName && shopName
    ? `Book your next appointment with ${stylistName} at ${shopName}. Skip the fees, pay direct via Zelle, PayPal, or Cash App.`
    : description;

  return (
    <Helmet>
      {/* Primary Meta Tags */}
      <title>{dynamicTitle}</title>
      <meta name="title" content={dynamicTitle} />
      <meta name="description" content={dynamicDescription} />

      {/* Open Graph / Facebook */}
      <meta property="og:type" content={type} />
      <meta property="og:url" content={url} />
      <meta property="og:title" content={dynamicTitle} />
      <meta property="og:description" content={dynamicDescription} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:site_name" content="MyHairChair" />

      {/* Twitter */}
      <meta name="twitter:card" content={twitterCard} />
      <meta name="twitter:url" content={url} />
      <meta name="twitter:title" content={dynamicTitle} />
      <meta name="twitter:description" content={dynamicDescription} />
      <meta name="twitter:image" content={ogImage} />

      {/* Additional SEO */}
      <meta name="robots" content="index, follow" />
      <meta name="language" content="English" />
      <meta name="author" content="MyHairChair" />
      
      {/* App specific */}
      <meta name="application-name" content="MyHairChair" />
      <meta name="apple-mobile-web-app-title" content="MyHairChair" />
      <meta name="theme-color" content="#f59e0b" />
    </Helmet>
  );
};

export default SEOMeta;

/**
 * Generate OpenGraph image URL for a stylist/barber profile
 * Uses portfolio images when available, falls back to generated cards
 */
export const generateOGImage = (userProfile) => {
  // Priority: 1. Portfolio image, 2. Profile image, 3. Default card image
  if (userProfile?.style_gallery?.length > 0) {
    return userProfile.style_gallery[0].image_url;
  }
  if (userProfile?.profile_picture) {
    return userProfile.profile_picture;
  }
  // Default based on professional type
  const type = userProfile?.professional_type?.toLowerCase() || 'stylist';
  const defaultImages = {
    stylist: "https://static.prod-images.emergentagent.com/jobs/2f9006eb-b332-428d-86e9-eb851b9d0232/images/63d58951e5ab7e31ae4c087a16e2dd8d29cd67a8c02b18e2b7a2b5eca234ad44.png",
    barber: "https://static.prod-images.emergentagent.com/jobs/2f9006eb-b332-428d-86e9-eb851b9d0232/images/aa29a7a60dd7530a808edfa970ef127c15e34d31eafd91409f265f4e14c47ee7.png",
    beautician: "https://static.prod-images.emergentagent.com/jobs/2f9006eb-b332-428d-86e9-eb851b9d0232/images/32e8be5d6280edba211d9172f3ca23a838a731bc26ff9efe64311cbc1fff5878.png",
    esthetician: "https://static.prod-images.emergentagent.com/jobs/2f9006eb-b332-428d-86e9-eb851b9d0232/images/32e8be5d6280edba211d9172f3ca23a838a731bc26ff9efe64311cbc1fff5878.png"
  };
  return defaultImages[type] || defaultImages.stylist;
};
